import React from 'react'
import PropTypes from 'prop-types'
import { onProps } from '@ds/react-utils'
import { CustomPropTypes } from '../../../support'
import { useThemeStyles } from '../../../theming'
import type { EventListenerProps, LabelForwardRef } from '../../../types'
import { InputRequiredAsterisk } from '../InputRequiredAsterisk'
import styles from './styles'

export interface InputLabelProps extends EventListenerProps<HTMLLabelElement> {
  /**
   * Optional accessibility string that overrides the label string.
   * `accessibilityText` replaces the visible `children` for screen reader users.
   */
  accessibilityText?: string
  /**
   * The InputLabel's text.
   */
  children: string
  /**
   * Enable dark mode (Ink only).
   */
  dark?: boolean
  /**
   * Accepts custom data attributes.
   */
  'data-qa'?: string
  /**
   * Disables the InputLabel.
   */
  disabled?: boolean
  /**
   * The 'id' of the associated control.
   */
  forId: string
  /**
   * A React ref to assign to the HTML node representing the Label element.
   */
  forwardedRef?: LabelForwardRef
  /**
   * Hides the InputLabel while allowing assistive devices to identify it.
   */
  hidden?: boolean
  /**
   * A unique identifier for the label element.
   */
  id?: string
  /**
   * Adds a visual treatment indicating that the associated control requires a value.
   */
  required?: boolean
}

export function InputLabel({
  accessibilityText,
  children,
  dark = false,
  disabled = false,
  forId,
  forwardedRef,
  hidden = false,
  id,
  required = false,
  'data-qa': dataQa,
  ...restProps
}: InputLabelProps) {
  const sx = useThemeStyles(styles, { dark })

  const requiredAsteriskNode = required && (
    <InputRequiredAsterisk data-qa={dataQa && `${dataQa}-required`} />
  )

  const textNode = accessibilityText ? (
    <>
      <span aria-hidden="true" data-qa={dataQa && `${dataQa}-text`}>
        {children}
      </span>
      <span css={sx.hidden} data-qa={dataQa && `${dataQa}-accessibility-text`}>
        {accessibilityText}
      </span>
    </>
  ) : (
    children
  )

  const labelStyles = [
    sx.default.label,
    disabled && sx.disabled.label,
    hidden && sx.hidden,
  ]

  return (
    <label
      {...onProps(restProps)}
      css={labelStyles}
      data-qa={dataQa}
      htmlFor={forId}
      id={id}
      ref={forwardedRef}
    >
      {textNode}
      {requiredAsteriskNode}
    </label>
  )
}

InputLabel.propTypes = {
  accessibilityText: PropTypes.string,
  children: PropTypes.string.isRequired,
  dark: PropTypes.bool,
  'data-qa': PropTypes.string,
  disabled: PropTypes.bool,
  forId: PropTypes.string.isRequired,
  forwardedRef: CustomPropTypes.ReactRef,
  hidden: PropTypes.bool,
  id: PropTypes.string,
  'on[A-Z].*': PropTypes.func,
  required: PropTypes.bool,
}

InputLabel.displayName = 'InputLabel'
